import { tzOpts } from "@/lib/tz";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AppAndroidAdmin from "@/components/AppAndroidAdmin";
import {
  Shield, RefreshCw, Plus, KeyRound, Pencil, Trash2, Send, MessageSquareHeart,
  Palette, Upload, X, Building2, CalendarClock, Copy, CheckCircle2, Layers, Users, Unlock, Megaphone, Smartphone,
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, PieChart as PieChartIcon,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  pending: "bg-orange-500/15 text-orange-300 border border-orange-500/30",
  suspended: "bg-yellow-500/15 text-yellow-300 border border-yellow-500/30",
  blocked: "bg-red-500/15 text-red-300 border border-red-500/30",
};

const STATUS_LABEL = {
  active: "Ativo", pending: "Pendente",
  suspended: "Suspenso", blocked: "Bloqueado",
};

const EMPTY_CREATE_FORM = {
  company_name: "", owner_name: "", email: "", phone: "", password: "",
  status: "active", plan_id: "", subscription_monthly: "49,90", subscription_day: 5, send_welcome: true,
  payment_exempt: false, marketing_module_enabled: false, app_module_enabled: false,
};

const EMPTY_PLAN_FORM = { name: "", min_clients: "", max_clients: "", price_cents: "" };

export default function Admin() {
  const [tenants, setTenants] = useState([]);
  const [openTenant, setOpenTenant] = useState(null);
  const [charges, setCharges] = useState([]);
  const [oxp, setOxp] = useState({ app_id: "", webhook_public_key_b64: "", base_url: "https://api.openpix.com.br" });
  const [welcome, setWelcome] = useState({ enabled: true, template: "", default_template: "" });
  const [reminder, setReminder] = useState({ enabled: true, template: "", default_template: "" });
  const [branding, setBranding] = useState({ site_title: "", favicon_data_uri: "", logo_data_uri: "" });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const iconInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [editing, setEditing] = useState(null); // tenant sendo editado
  const [unblocking, setUnblocking] = useState(null); // tenant sendo desbloqueado/reagendado
  const [unblockDate, setUnblockDate] = useState("");
  const [editForm, setEditForm] = useState({ name: "", contact_email: "", contact_phone: "", notes: "", payment_exempt: false, marketing_module_enabled: false, app_module_enabled: false });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null); // { email, password } - mostrado 1x após criar

  const [plans, setPlans] = useState([]);
  const [planChangeCfg, setPlanChangeCfg] = useState({ enabled: true, template: "", template_custom: "", default_template: "", default_template_custom: "" });
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null); // null = criando novo
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);
  const [marketingAddon, setMarketingAddon] = useState({ price_cents: 4990, tenants_using: 0 });
  const [appAddon, setAppAddon] = useState({ price_cents: 2990, tenants_using: 0 });
  const [financial, setFinancial] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const fmtMoney = (cents) => `R$ ${((cents || 0) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

  const load = async () => {
    try {
      const [{ data: ts }, { data: cfg }, { data: wc }, { data: rm }, { data: br }, { data: pl }, { data: pcc }, { data: ma }] = await Promise.all([
        api.get("/admin/tenants"),
        api.get("/admin/openpix/settings"),
        api.get("/admin/welcome"),
        api.get("/admin/subscription-reminder"),
        api.get("/admin/branding"),
        api.get("/admin/plans"),
        api.get("/admin/plan-change-notice"),
        api.get("/admin/modules/marketing"),
      ]);
      setTenants(ts);
      setOxp((o) => ({ ...o, ...cfg }));
      setWelcome(wc);
      setReminder(rm);
      setBranding(br);
      setPlans(pl);
      setPlanChangeCfg(pcc);
      setMarketingAddon(ma);
      // Separado do Promise.all: se o backend ainda nao tiver essa rota,
      // o restante da tela continua carregando normalmente.
      api.get("/admin/modules/app").then(({ data }) => setAppAddon(data)).catch(() => {});
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };
  useEffect(() => { load(); loadFinancial(); }, []);

  const loadFinancial = async () => {
    setFinancialLoading(true);
    try {
      const { data } = await api.get("/admin/financial-dashboard");
      setFinancial(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setFinancialLoading(false);
    }
  };

  // -------- Cadastro interno de empresa --------
  const openCreate = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreatedCreds(null);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!createForm.company_name.trim() || !createForm.email.trim()) {
      return toast.error("Informe ao menos o nome da empresa e o e-mail de acesso");
    }
    setCreating(true);
    try {
      const cents = Math.round(parseFloat(String(createForm.subscription_monthly).replace(",", ".")) * 100) || 4990;
      const { data } = await api.post("/admin/tenants", {
        company_name: createForm.company_name.trim(),
        owner_name: createForm.owner_name.trim(),
        email: createForm.email.trim(),
        phone: createForm.phone.trim(),
        password: createForm.password.trim(),
        status: createForm.status,
        plan_id: createForm.plan_id || null,
        subscription_monthly: cents,
        subscription_day: parseInt(createForm.subscription_day, 10) || 5,
        send_welcome: createForm.send_welcome,
        payment_exempt: createForm.payment_exempt,
        marketing_module_enabled: createForm.marketing_module_enabled,
        app_module_enabled: createForm.app_module_enabled,
      });
      toast.success(`Empresa "${createForm.company_name}" cadastrada`);
      if (data.welcome) {
        if (data.welcome.ok) toast.success(`Boas-vindas enviadas para ${data.welcome.phone}`);
        else toast.warning(`Boas-vindas não enviadas: ${data.welcome.error || data.welcome.skipped}`);
      }
      if (data.generated_password) {
        setCreatedCreds({ email: data.email, password: data.generated_password });
      } else {
        setCreateOpen(false);
      }
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setCreating(false);
    }
  };

  const copyCreds = () => {
    if (!createdCreds) return;
    navigator.clipboard?.writeText(`E-mail: ${createdCreds.email}\nSenha: ${createdCreds.password}`);
    toast.success("Credenciais copiadas");
  };

  // -------- Identidade visual --------
  const saveBrandingTitle = async () => {
    try {
      await api.put("/admin/branding", { site_title: branding.site_title });
      toast.success("Título da aba salvo");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const uploadBrandingIcon = async () => {
    const file = iconInputRef.current?.files?.[0];
    if (!file) return toast.error("Selecione um arquivo PNG, ICO, SVG ou JPG");
    if (file.size > 512 * 1024) return toast.error("Ícone muito grande (máx. 512KB)");
    setUploadingIcon(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/icon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ícone atualizado");
      if (iconInputRef.current) iconInputRef.current.value = "";
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setUploadingIcon(false);
    }
  };

  const removeBrandingIcon = async () => {
    try {
      await api.delete("/admin/branding/icon");
      toast.success("Ícone removido (voltou ao padrão)");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const uploadBrandingLogo = async () => {
    const file = logoInputRef.current?.files?.[0];
    if (!file) return toast.error("Selecione um arquivo PNG, SVG ou JPG");
    if (file.size > 1536 * 1024) return toast.error("Logo muito grande (máx. 1.5MB)");
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo atualizada");
      if (logoInputRef.current) logoInputRef.current.value = "";
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeBrandingLogo = async () => {
    try {
      await api.delete("/admin/branding/logo");
      toast.success("Logo removida (volta ao ícone padrão)");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  // -------- Empresas --------
  const setStatus = async (t, status) => {
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/status`, { status });
      toast.success(`Tenant ${t.name}: ${STATUS_LABEL[status]}`);
      if (data?.welcome) {
        if (data.welcome.ok) toast.success(`Boas-vindas enviadas para ${data.welcome.phone}`);
        else toast.warning(`Boas-vindas não enviadas: ${data.welcome.error || data.welcome.skipped}`);
      }
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const saveWelcome = async () => {
    try {
      await api.put("/admin/welcome", { enabled: welcome.enabled, template: welcome.template });
      toast.success("Mensagem de boas-vindas salva");
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const saveReminder = async () => {
    try {
      await api.put("/admin/subscription-reminder", { enabled: reminder.enabled, template: reminder.template, template_5d: reminder.template_5d });
      toast.success("Mensagem de lembrete de vencimento salva");
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const resendWelcome = async (t) => {
    if (!t.contact_phone) return toast.error("Tenant sem WhatsApp do responsável. Edite o tenant e informe o telefone.");
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/welcome`);
      toast.success(`Boas-vindas enviadas para ${data.phone}`);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const sendReminderNow = async (t) => {
    if (!t.contact_phone) return toast.error("Tenant sem WhatsApp do responsável. Edite o tenant e informe o telefone.");
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/billing/send-reminder`);
      if (data.whatsapp?.ok) toast.success(`Cobrança gerada e lembrete enviado para ${data.whatsapp.phone}`);
      else toast.warning(`Cobrança gerada, mas o WhatsApp falhou: ${data.whatsapp?.error || "erro desconhecido"}`);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const setSub = async (t, k, v) => {
    try {
      await api.put(`/admin/tenants/${t.id}/subscription`, { [k]: v });
      toast.success("Assinatura atualizada");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const setTenantPlan = async (t, planId) => {
    try {
      await api.put(`/admin/tenants/${t.id}`, { plan_id: planId || null });
      toast.success("Plano da empresa atualizado");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const checkPlanNow = async (t) => {
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/plan/check-now`);
      if (data.needs_custom_pricing) {
        toast.warning(`${t.name}: ${data.client_count} clientes ativos — acima do maior plano, precisa de negociação manual`);
      } else if (data.changed) {
        toast.success(`${t.name}: ${data.client_count} clientes ativos — plano atualizado de ${data.plan_from || "—"} para ${data.plan_to}`);
      } else {
        toast.success(`${t.name}: ${data.client_count} clientes ativos — dentro do limite do plano atual`);
      }
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  // -------- Planos --------
  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanFormOpen(true);
  };
  const openEditPlan = (p) => {
    setEditingPlan(p);
    setPlanForm({
      name: p.name,
      min_clients: String(p.min_clients),
      max_clients: p.max_clients == null ? "" : String(p.max_clients),
      price_cents: (p.price_cents / 100).toFixed(2).replace(".", ","),
    });
    setPlanFormOpen(true);
  };
  const submitPlan = async () => {
    if (!planForm.name.trim()) return toast.error("Informe o nome do plano");
    const body = {
      name: planForm.name.trim(),
      min_clients: parseInt(planForm.min_clients, 10) || 1,
      max_clients: planForm.max_clients === "" ? null : parseInt(planForm.max_clients, 10),
      price_cents: Math.round(parseFloat(String(planForm.price_cents).replace(",", ".")) * 100) || 0,
    };
    try {
      if (editingPlan) await api.put(`/admin/plans/${editingPlan._id}`, body);
      else await api.post("/admin/plans", body);
      toast.success("Plano salvo");
      setPlanFormOpen(false);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Excluir o plano "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/plans/${p._id}`);
      toast.success("Plano excluído");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const savePlanChangeCfg = async () => {
    try {
      await api.put("/admin/plan-change-notice", { enabled: planChangeCfg.enabled, template: planChangeCfg.template, template_custom: planChangeCfg.template_custom });
      toast.success("Aviso de mudança de plano salvo");
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const saveMarketingAddon = async () => {
    try {
      await api.put("/admin/modules/marketing", { price_cents: marketingAddon.price_cents });
      toast.success("Preço do módulo Marketing salvo");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const saveOxp = async () => {
    try {
      await api.put("/admin/openpix/settings", oxp);
      toast.success("OpenPix salva");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const loadCharges = async (t) => {
    setOpenTenant(t);
    try {
      const { data } = await api.get(`/admin/tenants/${t.id}/billing/charges`);
      setCharges(data);
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const deleteCharge = async (t, c) => {
    if (!window.confirm("Excluir esta cobrança? Se ainda estiver ativa, ela também será cancelada na OpenPix.")) return;
    try {
      await api.delete(`/admin/tenants/${t.id}/billing/charges/${c._id}`);
      toast.success("Cobrança excluída");
      loadCharges(t);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const createCharge = async (t) => {
    try {
      await api.post(`/admin/tenants/${t.id}/billing/charge`);
      toast.success("Cobrança criada");
      loadCharges(t);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const saveAppAddon = async () => {
    try {
      await api.put("/admin/modules/app", { price_cents: appAddon.price_cents });
      toast.success("Preço do módulo App salvo");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const startEdit = (t) => {
    setEditing(t);
    setEditForm({
      name: t.name || "",
      contact_email: t.contact_email || t.owner_email || "",
      contact_phone: t.contact_phone || "",
      notes: t.notes || "",
      payment_exempt: !!t.payment_exempt,
      marketing_module_enabled: !!t.marketing_module_enabled,
      app_module_enabled: !!t.app_module_enabled,
    });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/tenants/${editing.id}`, editForm);
      toast.success("Empresa atualizada");
      setEditing(null);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const startUnblock = (t) => {
    setUnblocking(t);
    const d = new Date();
    d.setDate(d.getDate() + 3);
    setUnblockDate(d.toISOString().slice(0, 10));
  };
  const confirmUnblock = async () => {
    try {
      const { data } = await api.post(`/admin/tenants/${unblocking.id}/unblock`, { new_due_date: unblockDate });
      toast.success(`${unblocking.name} desbloqueada — próximo vencimento reagendado para ${new Date(data.next_due_date + "T00:00:00").toLocaleDateString("pt-BR")}`);
      setUnblocking(null);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const deleteTenant = async (t) => {
    const answer = window.prompt(
      `Digite EXCLUIR (em maiúsculas) para remover DEFINITIVAMENTE a empresa "${t.name}" e todos os seus dados.`
    );
    if (answer !== "EXCLUIR") return;
    try {
      const { data } = await api.delete(`/admin/tenants/${t.id}`);
      toast.success(`Empresa removida (${Object.values(data.deleted || {}).reduce((a, b) => a + b, 0)} registros apagados)`);
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Superadmin</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Administração</h1>
          <p className="text-sm text-slate-500 mt-2">Cadastre empresas, gerencie a cobrança da assinatura e a identidade do sistema.</p>
        </div>
        <Button data-testid="reload-admin-btn" onClick={load} variant="outline" className="border-slate-700 hover:bg-slate-800 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-2" /> Recarregar
        </Button>
      </header>

      <Tabs defaultValue="empresas" className="w-full">
        <TabsList className="bg-slate-900/70 border border-slate-800 p-1 h-auto flex-wrap gap-1">
          <TabsTrigger value="empresas" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> Empresas
          </TabsTrigger>
          <TabsTrigger value="planos" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <Layers className="w-3.5 h-3.5 mr-1.5" /> Planos
          </TabsTrigger>
          <TabsTrigger value="cobranca" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Cobrança
          </TabsTrigger>
          <TabsTrigger value="boasvindas" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <MessageSquareHeart className="w-3.5 h-3.5 mr-1.5" /> Boas-vindas
          </TabsTrigger>
          <TabsTrigger value="identidade" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <Palette className="w-3.5 h-3.5 mr-1.5" /> Identidade Visual
          </TabsTrigger>
          <TabsTrigger value="financeiro" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <DollarSign className="w-3.5 h-3.5 mr-1.5" /> Financeiro
          </TabsTrigger>
        </TabsList>

        {/* ===================== EMPRESAS ===================== */}
        <TabsContent value="empresas" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500 max-w-2xl">
              O cadastro público foi desativado — empresas só entram no sistema por aqui.
            </p>
            <Button data-testid="open-create-tenant-btn" onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0">
              <Plus className="w-4 h-4 mr-1" /> Nova empresa
            </Button>
          </div>

          <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-3 px-4">Empresa</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Plano</th>
                  <th className="text-left py-3 px-4">Assinatura</th>
                  <th className="text-left py-3 px-4">Pagas / Total</th>
                  <th className="text-left py-3 px-4">Último Pgto</th>
                  <th className="text-left py-3 px-4">Ações</th>
                </tr>
              </thead>
              <tbody data-testid="tenants-tbody">
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="py-3 px-4 text-white cursor-default" title={t.owner_email}>{t.name}</td>
                    <td className="py-3 px-4">
                      <Badge className={STATUS_STYLES[t.status] || STATUS_STYLES.pending}>{STATUS_LABEL[t.status] || t.status}</Badge>
                      {t.payment_exempt && (
                        <Badge className="ml-1 bg-sky-500/15 text-sky-300 border border-sky-500/30">Isento</Badge>
                      )}
                      {t.marketing_module_enabled && (
                        <Badge className="ml-1 bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30">Marketing</Badge>
                      )}
                      {t.app_module_enabled && (
                        <Badge className="ml-1 bg-amber-500/15 text-amber-300 border border-amber-500/30">App</Badge>
                      )}
                      {t.status === "blocked" && t.blocked_reason === "overdue_payment" && (
                        <div className="text-[10px] text-red-400 mt-1">Bloqueado por atraso no pagamento</div>
                      )}
                      {t.next_due_date && (
                        <div className="text-[10px] text-slate-600 mt-0.5">Vence: {new Date(t.next_due_date + "T00:00:00").toLocaleDateString("pt-BR")}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <Select value={t.plan_id || "none"} onValueChange={(v) => setTenantPlan(t, v === "none" ? "" : v)}>
                          <SelectTrigger data-testid={`plan-select-${t.id}`} className="w-32 bg-slate-950 border-slate-800 text-white text-xs h-8">
                            <SelectValue placeholder="Sem plano" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                            <SelectItem value="none">Sem plano</SelectItem>
                            {plans.map((p) => (
                              <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button
                          data-testid={`plan-check-btn-${t.id}`}
                          onClick={() => checkPlanNow(t)}
                          title="Conferir agora quantos clientes ativos a empresa tem e ajustar o plano se necessário"
                          className="text-slate-400 hover:text-emerald-300 p-1"
                        >
                          <Users className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {t.plan_needs_custom_pricing && (
                        <div className="text-[10px] text-amber-400 mt-1">Acima de 2.000 — plano personalizado</div>
                      )}
                      {t.client_count_last_check != null && (
                        <div className="text-[10px] text-slate-600 mt-0.5">{t.client_count_last_check} clientes ativos</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400 text-xs">R$</span>
                        <input
                          data-testid={`sub-value-${t.id}`}
                          defaultValue={(t.subscription_monthly / 100).toFixed(2).replace(".", ",")}
                          onBlur={(e) => {
                            const cents = Math.round(parseFloat(e.target.value.replace(",", ".")) * 100);
                            if (cents && cents !== t.subscription_monthly) setSub(t, "subscription_monthly", cents);
                          }}
                          className="w-20 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-emerald-300 font-mono-jb text-xs"
                        />
                        <span className="text-slate-500 text-xs">dia</span>
                        <input
                          data-testid={`sub-day-${t.id}`}
                          defaultValue={t.subscription_day}
                          onBlur={(e) => {
                            const d = parseInt(e.target.value, 10);
                            if (d && d !== t.subscription_day) setSub(t, "subscription_day", d);
                          }}
                          className="w-10 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-emerald-300 font-mono-jb text-xs"
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono-jb text-xs text-slate-400">{t.charges_paid} / {t.charges_total}</td>
                    <td className="py-3 px-4 font-mono-jb text-xs text-slate-500">{t.last_paid_at ? new Date(t.last_paid_at).toLocaleDateString("pt-BR") : "—"}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                          <SelectTrigger data-testid={`status-select-${t.id}`} className="w-32 bg-slate-950 border-slate-800 text-white text-xs h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                            <SelectItem value="active">Ativar</SelectItem>
                            <SelectItem value="pending">Pendente</SelectItem>
                            <SelectItem value="suspended">Suspender</SelectItem>
                            <SelectItem value="blocked">Bloquear</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          data-testid={`charges-btn-${t.id}`}
                          onClick={() => loadCharges(t)}
                          className="text-xs text-emerald-400 hover:text-emerald-300 px-2"
                        >
                          Cobranças
                        </button>
                        <button
                          data-testid={`reminder-btn-${t.id}`}
                          onClick={() => sendReminderNow(t)}
                          title="Gerar cobrança da assinatura agora e enviar lembrete no WhatsApp da empresa"
                          className="text-slate-400 hover:text-emerald-300 p-1"
                        >
                          <CalendarClock className="w-4 h-4" />
                        </button>
                        {t.status === "blocked" && (
                          <button
                            data-testid={`unblock-btn-${t.id}`}
                            onClick={() => startUnblock(t)}
                            title="Desbloquear e reagendar o próximo vencimento"
                            className="text-red-400 hover:text-emerald-300 p-1"
                          >
                            <Unlock className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`welcome-btn-${t.id}`}
                          onClick={() => resendWelcome(t)}
                          title={t.welcome_sent_at ? `Boas-vindas enviadas em ${new Date(t.welcome_sent_at).toLocaleString("pt-BR", tzOpts())} — reenviar` : "Enviar boas-vindas (WhatsApp)"}
                          className={`p-1 ${t.welcome_sent_at ? "text-emerald-400 hover:text-emerald-300" : "text-slate-400 hover:text-amber-300"}`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`edit-tenant-${t.id}`}
                          onClick={() => startEdit(t)}
                          title="Editar"
                          className="text-slate-400 hover:text-emerald-300 p-1"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-tenant-${t.id}`}
                          onClick={() => deleteTenant(t)}
                          title="Excluir"
                          className="text-slate-400 hover:text-red-400 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-500">Nenhuma empresa cadastrada ainda.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ===================== PLANOS ===================== */}
        <TabsContent value="planos" className="space-y-6 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500 max-w-2xl">
              Faixas de clientes e mensalidade padrão. Ao cadastrar uma empresa com um plano, o valor da assinatura vem
              automaticamente daqui. Todo dia (8h50), o sistema confere quantos clientes ATIVOS cada empresa tem no mk-auth
              dela e, se ultrapassou o limite do plano atual, faz o upgrade sozinho e avisa a empresa pelo WhatsApp.
            </p>
            <Button data-testid="open-create-plan-btn" onClick={openCreatePlan} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0">
              <Plus className="w-4 h-4 mr-1" /> Novo plano
            </Button>
          </div>

          <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-3 px-4">Plano</th>
                  <th className="text-left py-3 px-4">Faixa de clientes</th>
                  <th className="text-left py-3 px-4">Mensalidade</th>
                  <th className="text-left py-3 px-4">Empresas usando</th>
                  <th className="text-left py-3 px-4">Ações</th>
                </tr>
              </thead>
              <tbody data-testid="plans-tbody">
                {plans.map((p) => (
                  <tr key={p._id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="py-3 px-4 text-white font-medium">{p.name}</td>
                    <td className="py-3 px-4 font-mono-jb text-slate-400 text-xs">
                      {p.min_clients} – {p.max_clients == null ? "∞" : p.max_clients}
                    </td>
                    <td className="py-3 px-4 font-mono-jb text-emerald-300 text-xs">R$ {(p.price_cents / 100).toFixed(2).replace(".", ",")}</td>
                    <td className="py-3 px-4 text-slate-400 text-xs">{p.tenants_using}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button data-testid={`edit-plan-${p._id}`} onClick={() => openEditPlan(p)} className="text-slate-400 hover:text-emerald-300 p-1" title="Editar">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`delete-plan-${p._id}`} onClick={() => deletePlan(p)} className="text-slate-400 hover:text-red-400 p-1" title="Excluir">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {plans.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-500">Nenhum plano cadastrado.</td></tr>
                )}
              </tbody>
            </table>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="plan-change-card">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-400" />
                <div className="font-outfit text-white font-semibold">Aviso de mudança de plano (WhatsApp da empresa)</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{planChangeCfg.enabled ? "Envio automático ativo" : "Desativado"}</span>
                <Switch
                  data-testid="plan-change-enabled-switch"
                  checked={!!planChangeCfg.enabled}
                  onCheckedChange={(v) => setPlanChangeCfg((c) => ({ ...c, enabled: v }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Mensagem de upgrade automático</Label>
              <p className="text-[11px] text-slate-600 mb-1">
                Variáveis: <span className="font-mono-jb text-emerald-300">{"{empresa} {plano_anterior} {plano_novo} {valor_novo} {qtd_clientes} {limite_anterior} {limite_novo}"}</span>
              </p>
              <Textarea
                data-testid="plan-change-template-input"
                value={planChangeCfg.template || ""}
                onChange={(e) => setPlanChangeCfg((c) => ({ ...c, template: e.target.value }))}
                rows={8}
                className="bg-slate-950 border-slate-800 text-slate-200 font-mono-jb text-xs"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Mensagem quando ultrapassa 2.000 clientes (plano personalizado)</Label>
              <Textarea
                data-testid="plan-change-custom-template-input"
                value={planChangeCfg.template_custom || ""}
                onChange={(e) => setPlanChangeCfg((c) => ({ ...c, template_custom: e.target.value }))}
                rows={5}
                className="bg-slate-950 border-slate-800 text-slate-200 font-mono-jb text-xs"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setPlanChangeCfg((c) => ({ ...c, template: c.default_template, template_custom: c.default_template_custom }))}
                className="border-slate-700 hover:bg-slate-800 hover:text-white"
              >
                Restaurar padrão
              </Button>
              <Button data-testid="plan-change-save-btn" onClick={savePlanChangeCfg} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                Salvar
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* ===================== COBRANÇA ===================== */}
        <TabsContent value="cobranca" className="space-y-6 mt-4">
          <Card className="bg-slate-900/50 border-slate-800 p-6">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <KeyRound className="w-4 h-4 text-emerald-400" />
              <div className="font-outfit text-white font-semibold">OpenPix (cobrança mensal dos clientes CobraZap)</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">AppID</Label>
                <Input data-testid="oxp-appid" type="password" value={oxp.app_id || ""} onChange={(e) => setOxp((o) => ({ ...o, app_id: e.target.value }))} placeholder="••••••••" className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Chave Pública Webhook (Base64)</Label>
                <Input data-testid="oxp-key" type="password" value={oxp.webhook_public_key_b64 || ""} onChange={(e) => setOxp((o) => ({ ...o, webhook_public_key_b64: e.target.value }))} placeholder="••••••••" className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Base URL</Label>
                <Input data-testid="oxp-url" value={oxp.base_url || ""} onChange={(e) => setOxp((o) => ({ ...o, base_url: e.target.value }))} className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-slate-500">
                Webhook OpenPix aponte para: <span className="font-mono-jb text-emerald-300">{process.env.REACT_APP_BACKEND_URL}/api/webhooks/openpix</span>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="oxp-register-webhook-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/openpix/webhook/register");
                      if (data.ok) toast.success("Webhook registrado na OpenPix");
                      else toast.error("Não registrou. Verifique o AppID e tente novamente.");
                    } catch (ex) {
                      toast.error(formatApiError(ex));
                    }
                  }}
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                >
                  Registrar Webhook
                </Button>
                <Button data-testid="oxp-save-btn" onClick={saveOxp} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">Salvar</Button>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="reminder-card">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-emerald-400" />
                <div className="font-outfit text-white font-semibold">Lembrete de vencimento da assinatura (WhatsApp da empresa)</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{reminder.enabled ? "Envio automático ativo" : "Desativado"}</span>
                <Switch
                  data-testid="reminder-enabled-switch"
                  checked={!!reminder.enabled}
                  onCheckedChange={(v) => setReminder((r) => ({ ...r, enabled: v }))}
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Ciclo automático da assinatura de cada empresa: a cobrança é gerada e o <b>lembrete de 5 dias antes</b> é
              enviado assim que faltam 5 dias pro vencimento; no <b>dia do vencimento</b>, se ainda não paga, reenvia o
              aviso; após <b>3 dias de atraso</b>, a empresa é bloqueada automaticamente (chatbot e cobranças param) até o
              pagamento ou desbloqueio manual. Ao pagar, o próximo ciclo já é criado sozinho. Variáveis:{" "}
              <span className="font-mono-jb text-emerald-300">{"{empresa} {valor} {vencimento} {pix_copia_cola} {link}"}</span>
            </p>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em] mb-1 block">Aviso 5 dias antes do vencimento</Label>
              <Textarea
                data-testid="reminder-template-5d-input"
                value={reminder.template_5d || ""}
                onChange={(e) => setReminder((r) => ({ ...r, template_5d: e.target.value }))}
                rows={8}
                className="bg-slate-950 border-slate-800 text-slate-200 font-mono-jb text-xs"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em] mb-1 block">Aviso no dia do vencimento</Label>
              <Textarea
                data-testid="reminder-template-input"
                value={reminder.template || ""}
                onChange={(e) => setReminder((r) => ({ ...r, template: e.target.value }))}
                rows={8}
                className="bg-slate-950 border-slate-800 text-slate-200 font-mono-jb text-xs"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                data-testid="reminder-reset-btn"
                variant="outline"
                onClick={() => setReminder((r) => ({ ...r, template: r.default_template, template_5d: r.default_template_5d }))}
                className="border-slate-700 hover:bg-slate-800 hover:text-white"
              >
                Restaurar padrão
              </Button>
              <Button data-testid="reminder-save-btn" onClick={saveReminder} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                Salvar
              </Button>
            </div>
            <p className="text-[11px] text-slate-600 pt-1 border-t border-slate-800/60">
              Quer testar fora do dia de vencimento ou cobrar antecipadamente uma empresa específica? Use o ícone{" "}
              <CalendarClock className="w-3 h-3 inline align-text-bottom text-emerald-400" /> na aba Empresas.
            </p>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="marketing-addon-card">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <Megaphone className="w-4 h-4 text-emerald-400" />
              <div className="font-outfit text-white font-semibold">Módulo Marketing (adicional pago)</div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Preço cobrado a mais, somado à mensalidade, para toda empresa com o módulo de Marketing habilitado
              (Empresas → Nova empresa/Editar). Mudar o valor aqui só afeta empresas cuja mensalidade for recalculada
              depois (plano trocado, módulo ativado/desativado) — não altera cobranças já geradas.
            </p>
            <div className="flex items-end gap-4">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Valor mensal do módulo R$</Label>
                <Input
                  data-testid="marketing-addon-price-input"
                  value={(marketingAddon.price_cents / 100).toFixed(2).replace(".", ",")}
                  onChange={(e) => {
                    const cents = Math.round(parseFloat(e.target.value.replace(",", ".")) * 100);
                    setMarketingAddon((m) => ({ ...m, price_cents: isNaN(cents) ? 0 : cents }));
                  }}
                  className="mt-1 bg-slate-950 border-slate-800 text-white w-32"
                />
              </div>
              <div className="text-sm text-slate-500 pb-2">{marketingAddon.tenants_using} empresa(s) com o módulo ativo hoje</div>
              <Button data-testid="marketing-addon-save-btn" onClick={saveMarketingAddon} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold ml-auto">
                Salvar
              </Button>
            </div>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="app-addon-card">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <Smartphone className="w-4 h-4 text-emerald-400" />
              <div className="font-outfit text-white font-semibold">Módulo App Android (adicional pago)</div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Libera o aplicativo CobraZap para Android. Só entra no app quem for de empresa com o módulo habilitado
              (Empresas → Nova empresa/Editar); ao desabilitar, o acesso pelo app é cortado na hora. O valor é somado
              à mensalidade quando ela for recalculada — não altera cobranças já geradas.
            </p>
            <div className="flex items-end gap-4">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Valor mensal do módulo R$</Label>
                <Input
                  data-testid="app-addon-price-input"
                  value={(appAddon.price_cents / 100).toFixed(2).replace(".", ",")}
                  onChange={(e) => {
                    const cents = Math.round(parseFloat(e.target.value.replace(",", ".")) * 100);
                    setAppAddon((m) => ({ ...m, price_cents: isNaN(cents) ? 0 : cents }));
                  }}
                  className="mt-1 bg-slate-950 border-slate-800 text-white w-32"
                />
              </div>
              <div className="text-sm text-slate-500 pb-2">{appAddon.tenants_using} empresa(s) com o módulo ativo hoje</div>
              <Button data-testid="app-addon-save-btn" onClick={saveAppAddon} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold ml-auto">
                Salvar
              </Button>
            </div>
          </Card>

          <AppAndroidAdmin />
        </TabsContent>

        {/* ===================== BOAS-VINDAS ===================== */}
        <TabsContent value="boasvindas" className="mt-4">
          <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="welcome-card">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquareHeart className="w-4 h-4 text-emerald-400" />
                <div className="font-outfit text-white font-semibold">Boas-vindas ao aprovar tenant (WhatsApp)</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{welcome.enabled ? "Envio automático ativo" : "Desativado"}</span>
                <Switch
                  data-testid="welcome-enabled-switch"
                  checked={!!welcome.enabled}
                  onCheckedChange={(v) => setWelcome((w) => ({ ...w, enabled: v }))}
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Ao mudar um tenant para <b>Ativo</b> (ou ao cadastrar uma empresa já como Ativa em Empresas → Nova empresa), esta
              mensagem é enviada pela <b>sua</b> instância Evolution (Configurações → Evolution do Superadmin) para o WhatsApp
              do responsável cadastrado no tenant. Variáveis:{" "}
              <span className="font-mono-jb text-emerald-300">{"{nome} {empresa} {email} {painel_url}"}</span>
            </p>
            <Textarea
              data-testid="welcome-template-input"
              value={welcome.template || ""}
              onChange={(e) => setWelcome((w) => ({ ...w, template: e.target.value }))}
              rows={12}
              className="bg-slate-950 border-slate-800 text-slate-200 font-mono-jb text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button
                data-testid="welcome-reset-btn"
                variant="outline"
                onClick={() => setWelcome((w) => ({ ...w, template: w.default_template }))}
                className="border-slate-700 hover:bg-slate-800 hover:text-white"
              >
                Restaurar padrão
              </Button>
              <Button data-testid="welcome-save-btn" onClick={saveWelcome} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                Salvar
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* ===================== IDENTIDADE VISUAL ===================== */}
        <TabsContent value="identidade" className="mt-4">
          <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="branding-card">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <Palette className="w-4 h-4 text-emerald-400" />
              <div className="font-outfit text-white font-semibold">Identidade Visual (aba do navegador)</div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Define o título da aba, o ícone (favicon) e a logo oficial usada na tela de login e no painel.
            </p>

            <div className="space-y-2 pb-2 border-b border-slate-800">
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Logo (tela de login e cabeçalho)</Label>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="h-12 min-w-[3rem] px-2 rounded border border-slate-800 bg-slate-950 flex items-center justify-center shrink-0 overflow-hidden">
                  {branding.logo_data_uri ? (
                    <img src={branding.logo_data_uri} alt="Logo atual" className="h-8 w-auto object-contain" />
                  ) : (
                    <Palette className="w-4 h-4 text-slate-600" />
                  )}
                </div>
                <Input
                  data-testid="branding-logo-input"
                  ref={logoInputRef}
                  type="file"
                  accept=".png,.svg,.jpg,.jpeg,image/png,image/svg+xml,image/jpeg"
                  className="bg-slate-950 border-slate-800 text-slate-300 text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-300 file:text-xs max-w-xs"
                />
                <Button
                  data-testid="branding-logo-upload-btn"
                  onClick={uploadBrandingLogo}
                  disabled={uploadingLogo}
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 shrink-0"
                >
                  <Upload className="w-4 h-4 mr-1" /> {uploadingLogo ? "Enviando…" : "Enviar"}
                </Button>
                {branding.logo_data_uri && (
                  <Button
                    data-testid="branding-logo-remove-btn"
                    onClick={removeBrandingLogo}
                    variant="outline"
                    title="Remover logo (volta ao ícone padrão)"
                    className="border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white shrink-0 px-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-600">PNG, SVG ou JPG · máx. 1.5MB · fundo transparente recomendado</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Título da aba</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="branding-title-input"
                    value={branding.site_title || ""}
                    onChange={(e) => setBranding((b) => ({ ...b, site_title: e.target.value }))}
                    placeholder="CobraZap"
                    maxLength={60}
                    className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
                  />
                  <Button
                    data-testid="branding-title-save-btn"
                    onClick={saveBrandingTitle}
                    className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0"
                  >
                    Salvar
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Ícone (favicon)</Label>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded border border-slate-800 bg-slate-950 flex items-center justify-center shrink-0 overflow-hidden">
                    {branding.favicon_data_uri ? (
                      <img src={branding.favicon_data_uri} alt="Favicon atual" className="w-6 h-6 object-contain" />
                    ) : (
                      <Palette className="w-4 h-4 text-slate-600" />
                    )}
                  </div>
                  <Input
                    data-testid="branding-icon-input"
                    ref={iconInputRef}
                    type="file"
                    accept=".png,.ico,.svg,.jpg,.jpeg,image/png,image/x-icon,image/svg+xml,image/jpeg"
                    className="bg-slate-950 border-slate-800 text-slate-300 text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-300 file:text-xs"
                  />
                  <Button
                    data-testid="branding-icon-upload-btn"
                    onClick={uploadBrandingIcon}
                    disabled={uploadingIcon}
                    variant="outline"
                    className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 shrink-0"
                  >
                    <Upload className="w-4 h-4 mr-1" /> {uploadingIcon ? "Enviando…" : "Enviar"}
                  </Button>
                  {branding.favicon_data_uri && (
                    <Button
                      data-testid="branding-icon-remove-btn"
                      onClick={removeBrandingIcon}
                      variant="outline"
                      title="Remover ícone (voltar ao padrão)"
                      className="border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white shrink-0 px-2"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-slate-600">PNG, ICO, SVG ou JPG · máx. 512KB</p>
              </div>
            </div>
          </Card>
        </TabsContent>
        {/* ===================== FINANCEIRO ===================== */}
        <TabsContent value="financeiro" className="mt-6 space-y-6">
          {financialLoading && !financial && (
            <div className="text-center py-10 text-slate-500 text-sm">Carregando…</div>
          )}
          {financial && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  Métricas do próprio negócio CobraZap (mensalidades das empresas-clientes) — atualizado às{" "}
                  {new Date(financial.generated_at).toLocaleTimeString("pt-BR", tzOpts())}
                </p>
                <Button variant="outline" size="sm" onClick={loadFinancial} disabled={financialLoading} className="border-slate-700 hover:bg-slate-800 hover:text-white">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${financialLoading ? "animate-spin" : ""}`} /> Atualizar
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 p-5">
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider">
                    <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> MRR
                  </div>
                  <div className="text-3xl font-outfit font-bold text-white mt-1">{fmtMoney(financial.mrr_cents)}</div>
                  <div className="text-[11px] text-slate-600 mt-1">ARR: {fmtMoney(financial.arr_cents)}</div>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 p-5">
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider">
                    <Building2 className="w-3.5 h-3.5 text-sky-400" /> Empresas ativas
                  </div>
                  <div className="text-3xl font-outfit font-bold text-white mt-1">{financial.totals.active}</div>
                  <div className="text-[11px] text-slate-600 mt-1">
                    {financial.totals.paying_active} pagantes · {financial.totals.exempt_active} isentas
                  </div>
                </Card>
                <Card className={`p-5 border ${financial.delinquency.rate > 0 ? "bg-red-500/5 border-red-500/30" : "bg-slate-900/50 border-slate-800"}`}>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider">
                    <AlertTriangle className={`w-3.5 h-3.5 ${financial.delinquency.rate > 0 ? "text-red-400" : "text-slate-500"}`} /> Inadimplência
                  </div>
                  <div className={`text-3xl font-outfit font-bold mt-1 ${financial.delinquency.rate > 0 ? "text-red-300" : "text-white"}`}>
                    {(financial.delinquency.rate * 100).toFixed(1)}%
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1">
                    {financial.delinquency.delinquent_count} de {financial.delinquency.paying_base} devendo
                  </div>
                </Card>
                <Card className={`p-5 border ${financial.churn.rate_30d > 0 ? "bg-amber-500/5 border-amber-500/30" : "bg-slate-900/50 border-slate-800"}`}>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider">
                    <TrendingDown className={`w-3.5 h-3.5 ${financial.churn.rate_30d > 0 ? "text-amber-400" : "text-slate-500"}`} /> Churn (30 dias)
                  </div>
                  <div className={`text-3xl font-outfit font-bold mt-1 ${financial.churn.rate_30d > 0 ? "text-amber-300" : "text-white"}`}>
                    {(financial.churn.rate_30d * 100).toFixed(1)}%
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1">
                    {financial.churn.blocked_last_30d} bloqueada(s) por atraso nos últimos 30 dias
                  </div>
                </Card>
              </div>

              <Card className="bg-slate-900/50 border-slate-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <PieChartIcon className="w-4 h-4 text-emerald-400" />
                  <div className="font-outfit text-white font-semibold">Empresas por plano</div>
                </div>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={financial.plans_breakdown} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="plan_name" tick={{ fill: "#94a3b8", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ color: "#e2e8f0" }}
                        formatter={(value, name) => name === "tenant_count" ? [value, "Empresas"] : [fmtMoney(value), "Receita"]}
                      />
                      <Bar dataKey="tenant_count" radius={[4, 4, 0, 0]}>
                        {financial.plans_breakdown.map((p, i) => (
                          <Cell key={i} fill={p.plan_id ? "#10b981" : "#64748b"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                      <tr>
                        <th className="text-left py-2 px-3">Plano</th>
                        <th className="text-left py-2 px-3">Empresas</th>
                        <th className="text-left py-2 px-3">Preço</th>
                        <th className="text-left py-2 px-3">Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financial.plans_breakdown.map((p) => (
                        <tr key={p.plan_id || "custom"} className="border-b border-slate-800/60">
                          <td className="py-2 px-3 text-white">{p.plan_name}</td>
                          <td className="py-2 px-3 font-mono-jb text-slate-300">{p.tenant_count}</td>
                          <td className="py-2 px-3 font-mono-jb text-slate-500">{p.price_cents != null ? fmtMoney(p.price_cents) : "variável"}</td>
                          <td className="py-2 px-3 font-mono-jb text-emerald-300">{fmtMoney(p.revenue_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <p className="text-[11px] text-slate-600 leading-relaxed">
                Inadimplência = empresas bloqueadas por atraso + ativas já vencidas, sobre o total de pagantes.
                Churn (30 dias) mede quantas empresas foram bloqueadas por atraso nos últimos 30 dias — hoje o
                sistema não distingue "bloqueada temporariamente" de "cancelamento definitivo" (uma empresa
                bloqueada volta ao pagar), então este número reflete taxa de bloqueio recente, não cancelamento
                permanente.
              </p>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* -------- Dialog: cobranças de um tenant -------- */}
      <Dialog open={!!openTenant} onOpenChange={(v) => !v && setOpenTenant(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white">Cobranças de {openTenant?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end mb-2">
            <Button onClick={() => createCharge(openTenant)} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold" size="sm">
              <Plus className="w-3 h-3 mr-1" /> Nova cobrança
            </Button>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-2 px-2">Criada</th>
                  <th className="text-left py-2 px-2">Valor</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Paga em</th>
                  <th className="text-left py-2 px-2">Link</th>
                  <th className="text-right py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c) => (
                  <tr key={c._id} className="border-b border-slate-800/60">
                    <td className="py-2 px-2 font-mono-jb text-slate-500">{new Date(c.created_at).toLocaleString("pt-BR", tzOpts())}</td>
                    <td className="py-2 px-2 font-mono-jb text-emerald-300">R$ {(c.value_cents / 100).toFixed(2).replace(".", ",")}</td>
                    <td className="py-2 px-2">{c.status}</td>
                    <td className="py-2 px-2 font-mono-jb text-slate-500">{c.paid_at ? new Date(c.paid_at).toLocaleString("pt-BR", tzOpts()) : "—"}</td>
                    <td className="py-2 px-2">{c.payment_link_url && <a href={c.payment_link_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300">abrir</a>}</td>
                    <td className="py-2 px-2 text-right">
                      <button
                        data-testid={`delete-charge-btn-${c._id}`}
                        onClick={() => deleteCharge(openTenant, c)}
                        className="text-red-400 hover:text-red-300 transition-colors duration-200"
                        title="Excluir cobrança"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {charges.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-slate-500">Nenhuma cobrança</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* -------- Dialog: editar empresa -------- */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg" data-testid="edit-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white">Editar empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Nome da empresa</Label>
              <Input
                data-testid="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1 bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">E-mail de contato</Label>
              <Input
                data-testid="edit-contact-email"
                value={editForm.contact_email}
                onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                className="mt-1 bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Telefone (WhatsApp - recebe boas-vindas e lembrete de vencimento)</Label>
              <Input
                data-testid="edit-contact-phone"
                value={editForm.contact_phone}
                onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                className="mt-1 bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Anotações internas</Label>
              <textarea
                data-testid="edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
                className="mt-1 w-full bg-slate-950 border border-slate-800 text-white rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center justify-between border border-slate-800 rounded-md px-3 py-2">
              <div>
                <Label className="text-slate-400 text-xs">Isentar do pagamento da assinatura</Label>
                <p className="text-[11px] text-slate-600">Nenhuma cobrança será gerada pra essa empresa, manual ou automática</p>
              </div>
              <Switch
                data-testid="edit-payment-exempt"
                checked={!!editForm.payment_exempt}
                onCheckedChange={(v) => setEditForm({ ...editForm, payment_exempt: v })}
              />
            </div>
            <div className="flex items-center justify-between border border-fuchsia-500/20 bg-fuchsia-500/5 rounded-md px-3 py-2">
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-fuchsia-400 shrink-0" />
                <div>
                  <Label className="text-slate-300 text-xs">Módulo Marketing</Label>
                  <p className="text-[11px] text-slate-500">
                    + R$ {(marketingAddon.price_cents / 100).toFixed(2).replace(".", ",")}/mês · recalcula a mensalidade ao salvar
                  </p>
                </div>
              </div>
              <Switch
                data-testid="edit-marketing-module"
                checked={!!editForm.marketing_module_enabled}
                onCheckedChange={(v) => setEditForm({ ...editForm, marketing_module_enabled: v })}
              />
            </div>
            <div className="flex items-center justify-between border border-amber-500/20 bg-amber-500/5 rounded-md px-3 py-2">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-amber-400 shrink-0" />
                <div>
                  <Label className="text-slate-300 text-xs">Módulo App Android</Label>
                  <p className="text-[11px] text-slate-500">
                    + R$ {(appAddon.price_cents / 100).toFixed(2).replace(".", ",")}/mês · recalcula a mensalidade ao salvar
                  </p>
                </div>
              </div>
              <Switch
                data-testid="edit-app-module"
                checked={!!editForm.app_module_enabled}
                onCheckedChange={(v) => setEditForm({ ...editForm, app_module_enabled: v })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-700 hover:bg-slate-800 hover:text-white">
              Cancelar
            </Button>
            <Button
              data-testid="edit-save-btn"
              onClick={saveEdit}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* -------- Dialog: desbloquear / reagendar -------- */}
      <Dialog open={!!unblocking} onOpenChange={(v) => !v && setUnblocking(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md" data-testid="unblock-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <Unlock className="w-4 h-4 text-emerald-400" /> Desbloquear {unblocking?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            A empresa foi bloqueada automaticamente por atraso no pagamento da assinatura. Escolha uma nova data de
            vencimento para reagendar o ciclo — sem isso, o próximo tick de cobrança bloquearia de novo imediatamente.
          </p>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Novo vencimento</Label>
            <Input
              data-testid="unblock-date-input"
              type="date"
              value={unblockDate}
              onChange={(e) => setUnblockDate(e.target.value)}
              className="mt-1 bg-slate-950 border-slate-800 text-white"
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setUnblocking(null)} className="border-slate-700 hover:bg-slate-800 hover:text-white">
              Cancelar
            </Button>
            <Button data-testid="unblock-confirm-btn" onClick={confirmUnblock} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              Desbloquear e reagendar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* -------- Dialog: nova empresa -------- */}
      <Dialog open={createOpen} onOpenChange={(v) => { if (!v) setCreateOpen(false); }}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg" data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-400" /> Nova empresa
            </DialogTitle>
          </DialogHeader>

          {createdCreds ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-300 text-sm">
                <CheckCircle2 className="w-4 h-4" /> Empresa cadastrada com sucesso.
              </div>
              <p className="text-xs text-slate-500">
                Uma senha foi gerada automaticamente. Ela só aparece aqui uma vez — copie e repasse ao responsável pela empresa
                por um canal seguro de sua preferência.
              </p>
              <div className="bg-slate-900 border border-slate-800 rounded-md p-4 space-y-2 font-mono-jb text-sm">
                <div><span className="text-slate-500">E-mail:</span> <span className="text-white">{createdCreds.email}</span></div>
                <div><span className="text-slate-500">Senha:</span> <span className="text-emerald-300">{createdCreds.password}</span></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={copyCreds} className="border-slate-700 hover:bg-slate-800 hover:text-white">
                  <Copy className="w-4 h-4 mr-1" /> Copiar
                </Button>
                <Button onClick={() => setCreateOpen(false)} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                  Concluir
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Nome da empresa *</Label>
                <Input
                  data-testid="create-company-name"
                  value={createForm.company_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, company_name: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Responsável</Label>
                  <Input
                    data-testid="create-owner-name"
                    value={createForm.owner_name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, owner_name: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">WhatsApp (com DDD)</Label>
                  <Input
                    data-testid="create-phone"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="5581999998888"
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">E-mail de acesso *</Label>
                <Input
                  data-testid="create-email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Senha (opcional)</Label>
                <Input
                  data-testid="create-password"
                  type="text"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Deixe em branco para gerar automaticamente"
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Plano</Label>
                <Select
                  value={createForm.plan_id || "manual"}
                  onValueChange={(v) => {
                    if (v === "manual") {
                      setCreateForm((f) => ({ ...f, plan_id: "" }));
                    } else {
                      const p = plans.find((pl) => pl._id === v);
                      setCreateForm((f) => ({ ...f, plan_id: v, subscription_monthly: p ? (p.price_cents / 100).toFixed(2).replace(".", ",") : f.subscription_monthly }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="create-plan" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                    <SelectValue placeholder="Sem plano (valor manual)" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                    <SelectItem value="manual">Sem plano (valor manual)</SelectItem>
                    {plans.map((p) => (
                      <SelectItem key={p._id} value={p._id}>
                        {p.name} — até {p.max_clients == null ? "∞" : p.max_clients} clientes — R$ {(p.price_cents / 100).toFixed(2).replace(".", ",")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Status inicial</Label>
                  <Select value={createForm.status} onValueChange={(v) => setCreateForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="create-status" className="mt-1 bg-slate-950 border-slate-800 text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="pending">Pendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Assinatura R$</Label>
                  <Input
                    data-testid="create-subscription-value"
                    value={createForm.subscription_monthly}
                    disabled={!!createForm.plan_id}
                    onChange={(e) => setCreateForm((f) => ({ ...f, subscription_monthly: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white disabled:opacity-60"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Dia vencimento</Label>
                  <Input
                    data-testid="create-subscription-day"
                    type="number" min={1} max={28}
                    value={createForm.subscription_day}
                    onChange={(e) => setCreateForm((f) => ({ ...f, subscription_day: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-800 text-white"
                  />
                </div>
              </div>
              {createForm.status === "active" && (
                <div className="flex items-center justify-between border border-slate-800 rounded-md px-3 py-2">
                  <Label className="text-slate-400 text-xs">Enviar boas-vindas por WhatsApp agora</Label>
                  <Switch
                    data-testid="create-send-welcome"
                    checked={createForm.send_welcome}
                    onCheckedChange={(v) => setCreateForm((f) => ({ ...f, send_welcome: v }))}
                  />
                </div>
              )}
              <div className="flex items-center justify-between border border-slate-800 rounded-md px-3 py-2">
                <div>
                  <Label className="text-slate-400 text-xs">Isentar do pagamento da assinatura</Label>
                  <p className="text-[11px] text-slate-600">Nenhuma cobrança será gerada pra essa empresa, manual ou automática</p>
                </div>
                <Switch
                  data-testid="create-payment-exempt"
                  checked={createForm.payment_exempt}
                  onCheckedChange={(v) => setCreateForm((f) => ({ ...f, payment_exempt: v }))}
                />
              </div>
              <div className="flex items-center justify-between border border-fuchsia-500/20 bg-fuchsia-500/5 rounded-md px-3 py-2">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-fuchsia-400 shrink-0" />
                  <div>
                    <Label className="text-slate-300 text-xs">Módulo Marketing</Label>
                    <p className="text-[11px] text-slate-500">
                      + R$ {(marketingAddon.price_cents / 100).toFixed(2).replace(".", ",")}/mês somado à mensalidade
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="create-marketing-module"
                  checked={createForm.marketing_module_enabled}
                  onCheckedChange={(v) => setCreateForm((f) => ({ ...f, marketing_module_enabled: v }))}
                />
              </div>
              <div className="flex items-center justify-between border border-amber-500/20 bg-amber-500/5 rounded-md px-3 py-2">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-amber-400 shrink-0" />
                  <div>
                    <Label className="text-slate-300 text-xs">Módulo App Android</Label>
                    <p className="text-[11px] text-slate-500">
                      + R$ {(appAddon.price_cents / 100).toFixed(2).replace(".", ",")}/mês somado à mensalidade
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="create-app-module"
                  checked={createForm.app_module_enabled}
                  onCheckedChange={(v) => setCreateForm((f) => ({ ...f, app_module_enabled: v }))}
                />
              </div>
              {(createForm.plan_id || createForm.marketing_module_enabled || createForm.app_module_enabled) && (
                <div className="text-xs text-slate-500 text-right -mt-1">
                  Mensalidade total estimada:{" "}
                  <span className="text-emerald-300 font-mono-jb">
                    R${" "}
                    {(
                      (createForm.plan_id
                        ? plans.find((p) => p._id === createForm.plan_id)?.price_cents || 0
                        : Math.round(parseFloat(String(createForm.subscription_monthly).replace(",", ".")) * 100) || 0) / 100 +
                      (createForm.marketing_module_enabled ? marketingAddon.price_cents / 100 : 0) +
                      (createForm.app_module_enabled ? appAddon.price_cents / 100 : 0)
                    ).toFixed(2).replace(".", ",")}
                  </span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-slate-700 hover:bg-slate-800 hover:text-white">
                  Cancelar
                </Button>
                <Button
                  data-testid="create-submit-btn"
                  onClick={submitCreate}
                  disabled={creating}
                  className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                >
                  {creating ? "Cadastrando…" : "Cadastrar empresa"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* -------- Dialog: novo/editar plano -------- */}
      <Dialog open={planFormOpen} onOpenChange={(v) => !v && setPlanFormOpen(false)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md" data-testid="plan-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-emerald-400" /> {editingPlan ? "Editar plano" : "Novo plano"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Nome</Label>
              <Input
                data-testid="plan-name-input"
                value={planForm.name}
                onChange={(e) => setPlanForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Mínimo de clientes</Label>
                <Input
                  data-testid="plan-min-input"
                  type="number" min={1}
                  value={planForm.min_clients}
                  onChange={(e) => setPlanForm((f) => ({ ...f, min_clients: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Máximo de clientes</Label>
                <Input
                  data-testid="plan-max-input"
                  type="number" min={1}
                  value={planForm.max_clients}
                  onChange={(e) => setPlanForm((f) => ({ ...f, max_clients: e.target.value }))}
                  placeholder="vazio = sem limite"
                  className="mt-1 bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Mensalidade R$</Label>
              <Input
                data-testid="plan-price-input"
                value={planForm.price_cents}
                onChange={(e) => setPlanForm((f) => ({ ...f, price_cents: e.target.value }))}
                className="mt-1 bg-slate-950 border-slate-800 text-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setPlanFormOpen(false)} className="border-slate-700 hover:bg-slate-800 hover:text-white">
              Cancelar
            </Button>
            <Button data-testid="plan-submit-btn" onClick={submitPlan} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
